import type { DirectionMaxRule, PortfolioBacktest, PositionSizeRule, TradingStrategyForDbExpanded } from '@einhaus/project314-types';
import { summarizeDirectionRules, summarizePositionSizeRules } from '@/utils/capitalRuleFormatters';

/**
 * The live portfolio state a frozen backtest snapshot is compared against. Structural on purpose:
 * the single-portfolio route and the all-portfolios route hand back differently shaped tRPC rows.
 */
interface LiveSubscribedStrategy {
    exitCandidateIds?: number[] | undefined;
    id: number;
    optionBuyingStrategyIds?: number[] | undefined;
    positionSizeRules?: PositionSizeRule[] | undefined;
}

export interface LivePortfolioConfiguration {
    settings?: { directionMaxRules?: DirectionMaxRule[] | undefined; positionSizeRules?: PositionSizeRule[] | undefined } | null;
    strategiesSubscribed?: readonly LiveSubscribedStrategy[] | null;
}

/**
 * `unknown` means the comparison could not be made — the live portfolio was not loaded, or the run
 * predates capital-snapshot recording. It is deliberately distinct from `matchesLive` so the UI can
 * stay silent instead of implying the run reproduces live trading.
 */
export type FrozenSnapshotDriftStatus = 'unknown' | 'matchesLive' | 'differsFromLive';

export interface FrozenSnapshotDrift {
    /** Human-readable list of the facets that diverged, used for the badge tooltip and the detail panel. */
    differences: string[];
    status: FrozenSnapshotDriftStatus;
}

export const UNKNOWN_FROZEN_SNAPSHOT_DRIFT: FrozenSnapshotDrift = { differences: [], status: 'unknown' };

const normalizeRuleValue = (value: number | string): string => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toString() : String(value);
};

const canonicalDirectionRule = (rule: DirectionMaxRule): string => `${rule.direction}|${rule.type}|${normalizeRuleValue(rule.value)}`;

const canonicalPositionSizeRule = (rule: PositionSizeRule): string =>
    `${rule.type}|${[...rule.entityTypes].sort().join(',')}|${normalizeRuleValue(rule.value)}`;

/** Rule ids are regenerated on edit, so equality is decided on rule semantics and not on identity or order. */
const canonicalRuleSet = <T>(rules: readonly T[] | undefined, canonicalize: (rule: T) => string): string =>
    (rules ?? []).map(canonicalize).sort().join(';');

const canonicalIdSet = (ids: readonly number[] | undefined): string => [...(ids ?? [])].sort((a, b) => a - b).join(',');

const strategyLabel = (strategyId: number, strategyNameMap: ReadonlyMap<number, string>): string =>
    strategyNameMap.get(strategyId) || `Strategy ${strategyId.toString()}`;

/**
 * Compares a run's frozen snapshot against the portfolio's current live deployment.
 *
 * The trading profile is intentionally excluded: it is an explicit per-run choice in the new-backtest
 * modal, so flagging it would mark nearly every research run as drifted and drown out the signal.
 */
export const compareFrozenSnapshotToLive = (
    backtest: PortfolioBacktest,
    livePortfolio: LivePortfolioConfiguration | undefined,
    strategyNameMap: ReadonlyMap<number, string>
): FrozenSnapshotDrift => {
    const liveSettings = livePortfolio?.settings;
    const liveStrategiesSubscribed = livePortfolio?.strategiesSubscribed;

    if (!liveSettings || !liveStrategiesSubscribed) {
        return UNKNOWN_FROZEN_SNAPSHOT_DRIFT;
    }

    const frozenDirectionMaxRules = backtest.settings.directionMaxRules;
    const frozenPositionSizeRules = backtest.settings.positionSizeRules;

    if (frozenDirectionMaxRules === undefined || frozenPositionSizeRules === undefined) {
        return UNKNOWN_FROZEN_SNAPSHOT_DRIFT;
    }

    const differences: string[] = [];

    if (
        canonicalRuleSet(frozenDirectionMaxRules, canonicalDirectionRule) !==
        canonicalRuleSet(liveSettings.directionMaxRules, canonicalDirectionRule)
    ) {
        differences.push('Position and exposure limits');
    }

    if (
        canonicalRuleSet(frozenPositionSizeRules, canonicalPositionSizeRule) !==
        canonicalRuleSet(liveSettings.positionSizeRules, canonicalPositionSizeRule)
    ) {
        differences.push('Default position sizing');
    }

    const liveStrategyMap = new Map(liveStrategiesSubscribed.map((strategy) => [strategy.id, strategy]));

    for (const frozenStrategy of backtest.strategiesSubscribed) {
        const label = strategyLabel(frozenStrategy.id, strategyNameMap);
        const liveStrategy = liveStrategyMap.get(frozenStrategy.id);

        if (!liveStrategy) {
            differences.push(`${label}: no longer subscribed to this portfolio`);
            continue;
        }

        if (canonicalIdSet(frozenStrategy.optionBuyingStrategyIds) !== canonicalIdSet(liveStrategy.optionBuyingStrategyIds)) {
            differences.push(`${label}: option buying strategies`);
        }

        if (canonicalIdSet(frozenStrategy.exitCandidateIds) !== canonicalIdSet(liveStrategy.exitCandidateIds)) {
            differences.push(`${label}: exit candidates`);
        }

        if (
            canonicalRuleSet(frozenStrategy.portfolioBacktestPositionSizeRules, canonicalPositionSizeRule) !==
            canonicalRuleSet(liveStrategy.positionSizeRules, canonicalPositionSizeRule)
        ) {
            differences.push(`${label}: position sizing`);
        }
    }

    return { differences, status: differences.length > 0 ? 'differsFromLive' : 'matchesLive' };
};

export interface FrozenCapitalChip {
    label: string;
    /** `missing` marks a run that predates capital-snapshot recording, where nothing can be inferred. */
    isRecorded: boolean;
    value: string;
}

export const buildFrozenCapitalChips = (backtest: PortfolioBacktest): FrozenCapitalChip[] => {
    const { directionMaxRules, positionSizeRules } = backtest.settings;

    return [
        {
            label: 'Position limits',
            isRecorded: directionMaxRules !== undefined,
            value: directionMaxRules ? summarizeDirectionRules(directionMaxRules) : 'Not recorded'
        },
        {
            label: 'Default sizing',
            isRecorded: positionSizeRules !== undefined,
            value: positionSizeRules ? summarizePositionSizeRules(positionSizeRules) : 'Not recorded'
        }
    ];
};

/** One-line capital signature for the optional `Capital` column. */
export const summarizeFrozenCapital = (backtest: PortfolioBacktest): string =>
    buildFrozenCapitalChips(backtest)
        .map((chip) => chip.value)
        .join(' · ');

export interface FrozenSelectionChip {
    label: string;
    /** Spelled-out meaning of the abbreviated label plus the itemised selections, surfaced as the chip tooltip. */
    title: string;
}

export interface FrozenSelectionNameMaps {
    /** Exit candidate id → server label; entries appear as the hydration query resolves. */
    exitCandidateLabelMap: ReadonlyMap<number, string>;
    optionBuyingStrategyNameMap: ReadonlyMap<number, string>;
    strategyNameMap: ReadonlyMap<number, string>;
}

const describeFrozenReference = (id: number, nameMap: ReadonlyMap<number, string>): string => {
    const name = nameMap.get(id);

    return name ? `${name} (#${id.toString()})` : `#${id.toString()}`;
};

interface FrozenSelectionGroup {
    strategyLabel: string;
    items: string[];
}

/** One line per item for a single-strategy run; one labelled line per strategy once several strategies contribute. */
const itemizeSelectionDetail = (groups: readonly FrozenSelectionGroup[], useStrategyLabels: boolean): string[] =>
    useStrategyLabels
        ? groups.map((group) => `${group.strategyLabel}: ${group.items.join(' · ')}`)
        : groups.flatMap((group) => group.items);

/**
 * Compact counts of what the run pinned, so the collapsed cell says how much was frozen without
 * listing every id. The labels are abbreviated to fit the column, so each carries a tooltip that
 * spells the abbreviation out and itemises the frozen selections by name and id.
 */
export const buildFrozenSelectionChips = (backtest: PortfolioBacktest, nameMaps: FrozenSelectionNameMaps): FrozenSelectionChip[] => {
    const optionBuyingStrategyGroups: FrozenSelectionGroup[] = [];
    const exitCandidateGroups: FrozenSelectionGroup[] = [];
    const sizingOverrideGroups: FrozenSelectionGroup[] = [];

    for (const strategy of backtest.strategiesSubscribed) {
        const label = strategyLabel(strategy.id, nameMaps.strategyNameMap);

        if (strategy.optionBuyingStrategyIds?.length) {
            optionBuyingStrategyGroups.push({
                strategyLabel: label,
                items: strategy.optionBuyingStrategyIds.map((id) => describeFrozenReference(id, nameMaps.optionBuyingStrategyNameMap))
            });
        }

        if (strategy.exitCandidateIds?.length) {
            exitCandidateGroups.push({
                strategyLabel: label,
                items: strategy.exitCandidateIds.map((id) => describeFrozenReference(id, nameMaps.exitCandidateLabelMap))
            });
        }

        if (strategy.portfolioBacktestPositionSizeRules?.length) {
            sizingOverrideGroups.push({
                strategyLabel: label,
                items: [summarizePositionSizeRules(strategy.portfolioBacktestPositionSizeRules)]
            });
        }
    }

    const optionBuyingStrategyCount = optionBuyingStrategyGroups.reduce((count, group) => count + group.items.length, 0);
    const exitCandidateCount = exitCandidateGroups.reduce((count, group) => count + group.items.length, 0);
    const sizingOverrideCount = sizingOverrideGroups.length;
    const useStrategyLabels = backtest.strategiesSubscribed.length > 1;

    const chips: FrozenSelectionChip[] = [];

    if (optionBuyingStrategyCount > 0) {
        const headline =
            optionBuyingStrategyCount === 1
                ? '1 option buying strategy frozen into this run'
                : `${optionBuyingStrategyCount.toLocaleString()} option buying strategies frozen into this run`;

        chips.push({
            label: `OBS ${optionBuyingStrategyCount.toLocaleString()}`,
            title: [headline, ...itemizeSelectionDetail(optionBuyingStrategyGroups, useStrategyLabels)].join('\n')
        });
    }

    if (exitCandidateCount > 0) {
        const headline =
            exitCandidateCount === 1
                ? '1 exit candidate frozen into this run'
                : `${exitCandidateCount.toLocaleString()} exit candidates frozen into this run`;

        chips.push({
            label: `Exits ${exitCandidateCount.toLocaleString()}`,
            title: [headline, ...itemizeSelectionDetail(exitCandidateGroups, useStrategyLabels)].join('\n')
        });
    }

    if (sizingOverrideCount > 0) {
        const headline =
            sizingOverrideCount === 1
                ? "1 strategy overrides the run's default position sizing"
                : `${sizingOverrideCount.toLocaleString()} strategies override the run's default position sizing`;

        chips.push({
            label: sizingOverrideCount === 1 ? 'Sizing override' : `${sizingOverrideCount.toLocaleString()} sizing overrides`,
            title: [headline, ...itemizeSelectionDetail(sizingOverrideGroups, useStrategyLabels)].join('\n')
        });
    }

    return chips;
};

export type FrozenStrategySizingKind = 'override' | 'backtestDefault' | 'notRecorded';

export interface FrozenStrategySizing {
    kind: FrozenStrategySizingKind;
    text: string;
}

export const describeFrozenStrategySizing = (
    strategy: TradingStrategyForDbExpanded,
    defaultRulesWereRecorded: boolean
): FrozenStrategySizing => {
    const rules = strategy.portfolioBacktestPositionSizeRules;

    if (rules?.length) {
        return { kind: 'override', text: summarizePositionSizeRules(rules) };
    }

    return defaultRulesWereRecorded
        ? { kind: 'backtestDefault', text: 'Backtest default' }
        : { kind: 'notRecorded', text: 'Not recorded at backtest level' };
};

/** Extra search tokens so ids stay findable once the cell stops listing them. */
export const buildFrozenSnapshotSearchTokens = (backtest: PortfolioBacktest): string[] => {
    const tokens: string[] = [];

    for (const strategy of backtest.strategiesSubscribed) {
        for (const optionBuyingStrategyId of strategy.optionBuyingStrategyIds ?? []) {
            tokens.push(`obs:${optionBuyingStrategyId.toString()}`, `#${optionBuyingStrategyId.toString()}`);
        }

        for (const exitCandidateId of strategy.exitCandidateIds ?? []) {
            tokens.push(`exit:${exitCandidateId.toString()}`, `#${exitCandidateId.toString()}`);
        }
    }

    return tokens;
};
